/**
 * ⚙️ Station Settings Tab - Cetalingua FM
 * 
 * - Current plan info
 * - Storage usage & purchase
 * - Billing history
 * - Cancel/manage subscription via Stripe
 *
 * UPDATES:
 * ✅ FIXED: Usage path uses stations/{stationId} instead of stations/{user.uid}
 */

import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../firebase-config';
import portalPlans from '../../portal/plans.json';
import './StationSettings.css';

const MONTHLY_LIMIT_GB = 3;
const EXTRA_GB_PRICE = portalPlans.storage?.priceUsd || 5;   // the real price comes from Stripe

const StationSettings = ({ user, stationData }) => {
  const [usageData, setUsageData] = useState({ used: 0, purchased: 0 });
  const [billingHistory, setBillingHistory] = useState([]);
  const [purchaseAmount, setPurchaseAmount] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);

  // Stage 3: plan / status from the portal subscription (users/{uid}), not from the station record
  const [account, setAccount] = useState(null);
  const storageMsg = new URLSearchParams(window.location.search).get('storage');
  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid)).then((s) => setAccount(s.data() || {})).catch(() => setAccount({}));
  }, [user]);
  const sub = account?.portalSubscription;
  const subPlan = sub?.plan ? portalPlans.plans.find((p) => p.id === sub.plan) : null;
  const subActive = ['active', 'trialing', 'past_due'].includes(sub?.status);
  const freeAccess = !subActive && (account?.products || []).includes('station');

  useEffect(() => {
    loadUsageData();
    loadBillingHistory();
  }, [user, stationData]);

  const loadUsageData = async () => {
    // ✅ FIXED: Use stationId instead of user.uid
    const stationId = stationData?.stationId ? String(stationData.stationId) : null;
    if (!stationId) {
      console.warn('⚠️ No stationId available for usage lookup');
      return;
    }

    try {
      const now = new Date();
      const monthKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;   // UTC month, as the server counts
      const usageRef = doc(db, 'stations', stationId, 'usage', monthKey);
      const usageDoc = await getDoc(usageRef);

      if (usageDoc.exists()) {
        setUsageData(usageDoc.data());
      }
    } catch (error) {
      console.error('Error loading usage:', error);
    }
  };

  const loadBillingHistory = async () => {
    try {
      const paymentsRef = collection(db, 'users', user.uid, 'payments');
      const q = query(paymentsRef, orderBy('createdAt', 'desc'), limit(10));
      const snap = await getDocs(q);

      const history = [];
      snap.forEach(doc => {
        history.push({ id: doc.id, ...doc.data() });
      });
      setBillingHistory(history);
    } catch (error) {
      console.error('Error loading billing:', error);
    }
  };

  const formatBytes = (bytes) => {
    const gb = bytes / (1024 * 1024 * 1024);
    return gb.toFixed(2) + ' GB';
  };

  const getUsagePercentage = () => {
    const totalGB = MONTHLY_LIMIT_GB + (usageData.purchased || 0);
    const usedGB = (usageData.used || 0) / (1024 * 1024 * 1024);
    return Math.min(100, (usedGB / totalGB) * 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handlePurchaseStorage = async () => {
    setPurchasing(true);
    try {
      const functions = getFunctions();
      const createCheckout = httpsCallable(functions, 'createStorageCheckout');

      const { data } = await createCheckout({
        stationId: stationData?.stationId,
        gigabytes: purchaseAmount,
      });

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to start checkout: ' + error.message);
    } finally {
      setPurchasing(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const functions = getFunctions();
      const createPortal = httpsCallable(functions, 'createPortalSession');

      const { data } = await createPortal();

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Portal error:', error);
      alert('Failed to open billing portal: ' + error.message);
    } finally {
      setLoadingPortal(false);
    }
  };

  const usagePercent = getUsagePercentage();
  const usedGB = (usageData.used || 0) / (1024 * 1024 * 1024);
  const totalGB = MONTHLY_LIMIT_GB + (usageData.purchased || 0);
  const availableGB = Math.max(0, totalGB - usedGB);

  return (
    <div className="station-settings-tab">
      {/* Plan Card */}
      <div className="settings-card plan-card">
        <div className="card-header">
          <h3>📡 Your Plan</h3>
          <span className="plan-badge">{subPlan?.name || (freeAccess ? 'Free access' : 'Station Host')}</span>
        </div>

        {subPlan && (
          <div className="plan-price">
            <span className="price">${subPlan.prices[sub.period]}</span>
            <span className="period">{portalPlans.periods[sub.period]?.months === 1 ? '/month' : ` / ${portalPlans.periods[sub.period]?.months} months`}</span>
          </div>
        )}

        <ul className="plan-features">
          <li>✓ Station visible on global map</li>
          <li>✓ 3 GB monthly upload included</li>
          <li>✓ Species detection processing</li>
          <li>✓ Listener analytics dashboard</li>
          <li>✓ Premium mobile app access</li>
        </ul>

        <div className="plan-status-row">
          <span className="label">Status:</span>
          <span className={`status ${subActive || freeAccess ? 'active' : 'pending'}`}>
            {subActive || freeAccess ? '🟢 Active' : '🟡 No active plan'}
          </span>
        </div>

        {subActive && sub.currentPeriodEnd && (
          <div className="plan-status-row">
            <span className="label">{sub.cancelAtPeriodEnd ? 'Ends on:' : 'Next billing:'}</span>
            <span>{formatDate(sub.currentPeriodEnd)}</span>
          </div>
        )}

        <button 
          className="manage-btn" 
          onClick={handleManageSubscription}
          disabled={loadingPortal}
        >
          {loadingPortal ? 'Loading...' : 'Manage Subscription'}
        </button>
      </div>

      {/* Storage Card */}
      <div className="settings-card storage-card">
        <div className="card-header">
          <h3>💾 Storage</h3>
          <span className="month-label">
            {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
        </div>

        <div className="storage-bar-wrapper">
          <div
            className={`storage-bar ${usagePercent > 80 ? 'warning' : ''} ${usagePercent > 95 ? 'critical' : ''}`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>

        <div className="storage-stats">
          <span>{usedGB.toFixed(2)} GB used</span>
          <span>of {totalGB} GB</span>
        </div>

        <div className="storage-breakdown">
          <div className="breakdown-row">
            <span>Included:</span>
            <span>{MONTHLY_LIMIT_GB} GB</span>
          </div>
          <div className="breakdown-row">
            <span>Purchased:</span>
            <span>{usageData.purchased || 0} GB</span>
          </div>
          <div className="breakdown-row highlight">
            <span>Available:</span>
            <span>{availableGB.toFixed(2)} GB</span>
          </div>
        </div>

        {/* Purchase Storage */}
        <div className="purchase-box">
          <h4>Buy More Storage</h4>
          <div className="purchase-row">
            <div className="quantity-control">
              <button onClick={() => setPurchaseAmount(Math.max(1, purchaseAmount - 1))}>−</button>
              <span>{purchaseAmount} GB</span>
              <button onClick={() => setPurchaseAmount(purchaseAmount + 1)}>+</button>
            </div>
            <span className="purchase-price">${purchaseAmount * EXTRA_GB_PRICE}</span>
            <button
              className="purchase-btn"
              onClick={handlePurchaseStorage}
              disabled={purchasing}
            >
              {purchasing ? 'Processing...' : 'Purchase'}
            </button>
          </div>
          <p className="purchase-note">${EXTRA_GB_PRICE} per GB · one-time payment · added to this month's allowance after payment</p>
          {storageMsg === 'success' && <p className="purchase-note" style={{ color: '#16a34a' }}>✓ Payment received — the extra GB appear here within a few seconds (reload if not).</p>}
        </div>
      </div>

      {/* Account Card */}
      <div className="settings-card account-card">
        <h3>👤 Account</h3>

        <div className="account-row">
          <span className="label">Station ID:</span>
          <span className="value mono">{stationData?.stationId || 'Pending'}</span>
        </div>
        <div className="account-row">
          <span className="label">Email:</span>
          <span className="value">{user?.email || 'Not set'}</span>
        </div>
        <div className="account-row">
          <span className="label">Member since:</span>
          <span className="value">{formatDate(stationData?.createdAt)}</span>
        </div>
      </div>

      {/* Billing History */}
      <div className="settings-card billing-card">
        <h3>📄 Billing History</h3>

        {billingHistory.length > 0 ? (
          <table className="billing-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {billingHistory.map(payment => (
                <tr key={payment.id}>
                  <td>{formatDate(payment.createdAt)}</td>
                  <td>{payment.description || 'Station subscription'}</td>
                  <td>${((payment.amount || 0) / 100).toFixed(2)}</td>
                  <td>
                    <span className={`payment-status ${payment.status}`}>
                      {payment.status === 'succeeded' ? '✓ Paid' : payment.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="no-billing">No billing history yet</p>
        )}
      </div>

      {/* Danger Zone */}
      <div className="settings-card danger-card">
        <h3>⚠️ Danger Zone</h3>
        <p>Cancel your subscription. Your data will be retained for 30 days.</p>
        <button className="danger-btn" onClick={handleManageSubscription}>
          Cancel Subscription
        </button>
      </div>
    </div>
  );
};

export default StationSettings;
